import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useNotify } from "react-admin";
import { organizationsApi, rolesApi } from "../providers/env";
import { userApi } from "../providers/env";
import { OrganizationOut, RoleOut } from "../generated";

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  currentUserIsSuperAdmin: boolean;
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({
  open,
  onClose,
  onSuccess,
  currentUserIsSuperAdmin,
}) => {
  const notify = useNotify();
  const [saving, setSaving] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationOut[]>([]);
  const [roles, setRoles] = useState<RoleOut[]>([]);
  const [userLevel, setUserLevel] = useState<string>("user");
  const [emailError, setEmailError] = useState<string>("");
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    organization_id: "",
    role_id: "",
  });

  useEffect(() => {
    if (open) {
      setForm({
        full_name: "",
        email: "",
        password: "",
        organization_id: "",
        role_id: "",
      });
      setUserLevel("user");
      setEmailError("");
      if (currentUserIsSuperAdmin) {
        fetchOrganizations();
      } else {
        // If not superadmin, get current user's org
        loadCurrentUserOrganization();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentUserIsSuperAdmin]);

  const fetchOrganizations = async () => {
    try {
      const response = await organizationsApi.getOrganizations();
      setOrganizations(response.data || []);
    } catch (error: any) {
      notify("Error loading organizations", { type: "error" });
    }
  };

  const loadCurrentUserOrganization = () => {
    try {
      const permissions = localStorage.getItem("permissions");
      if (permissions) {
        const userInfo = JSON.parse(permissions);
        if (userInfo.organization_id) {
          setForm((prev) => ({
            ...prev,
            organization_id: userInfo.organization_id,
          }));
        }
      }
    } catch (error) {
      console.error("Error loading user organization", error);
    }
  };

  const fetchRoles = async (orgId?: string) => {
    try {
      const params = orgId ? { organizationId: orgId } : {};
      const response = await rolesApi.getRoles(params);
      setRoles(response.data || []);
    } catch (error: any) {
      notify("Error loading roles", { type: "error" });
    }
  };

  useEffect(() => {
    if (form.organization_id) {
      fetchRoles(form.organization_id);
      // Reset role selection when org changes (unless super_admin)
      if (userLevel !== "super_admin") {
        setForm((prev) => ({ ...prev, role_id: "" }));
      }
    } else {
      setRoles([]);
      setForm((prev) => ({ ...prev, role_id: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.organization_id]);

  // Auto-select super_admin role when roles are loaded and user level is super_admin
  useEffect(() => {
    if (userLevel === "super_admin" && roles.length > 0) {
      const superAdminRole = roles.find((r) => r.user_level === "super_admin");
      if (superAdminRole) {
        setForm((prev) => ({ ...prev, role_id: superAdminRole.id }));
      }
    }
  }, [roles, userLevel]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const name = e.target.name as string;
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [name]: value }));

    // Validate email on change
    if (name === "email") {
      const emailValue = value as string;
      if (emailValue && !validateEmail(emailValue)) {
        setEmailError("Please enter a valid email address");
      } else {
        setEmailError("");
      }
    }
  };

  const handleUserLevelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const level = e.target.value;
    setUserLevel(level);

    // For super_admin, auto-select the super_admin role
    if (level === "super_admin") {
      const superAdminRole = roles.find((r) => r.user_level === "super_admin");
      if (superAdminRole) {
        setForm((prev) => ({ ...prev, role_id: superAdminRole.id }));
      }
    } else {
      // Reset role selection when level changes
      setForm((prev) => ({ ...prev, role_id: "" }));
    }
  };

  // Detect role level from its user_level field OR permissions (handles old data)
  const getRoleLevel = (role: RoleOut): string => {
    const level = role.user_level?.toLowerCase();
    if (level === "super_admin" || level === "sub_admin") return level;

    // Fallback: check permissions for old data where user_level is wrong
    const perms = role.permissions || [];
    const hasRoleMgmt = perms.some(
      (p: any) => p.resource === "role" && ["create", "edit", "delete"].includes(p.action)
    );
    const hasUserMgmt = perms.some(
      (p: any) => p.resource === "user" && ["create", "edit"].includes(p.action)
    );
    if (hasRoleMgmt) return "super_admin";
    if (hasUserMgmt) return "sub_admin";
    return "user";
  };

  // Filter roles based on selected user level
  const getFilteredRoles = (): RoleOut[] => {
    if (!roles || roles.length === 0) return [];
    return roles.filter((role) => getRoleLevel(role) === userLevel);
  };

  const handleSubmit = async () => {
    if (!form.email.trim()) {
      notify("Email is required", { type: "warning" });
      return;
    }
    if (!validateEmail(form.email)) {
      notify("Please enter a valid email address", { type: "warning" });
      setEmailError("Please enter a valid email address");
      return;
    }
    if (!form.password.trim()) {
      notify("Password is required", { type: "warning" });
      return;
    }
    if (!form.organization_id) {
      notify("Organization is required", { type: "warning" });
      return;
    }
    // Role validation (not required for super_admin - auto assigned)
    if (userLevel !== "super_admin" && !form.role_id) {
      notify("Role is required", { type: "warning" });
      return;
    }

    setSaving(true);
    try {
      const createData: any = {
        email: form.email,
        password: form.password,
        full_name: form.full_name || undefined,
        organization_id: form.organization_id,
        is_active: true,
      };

      // Set role and is_superuser based on user level
      if (userLevel === "super_admin") {
        createData.is_superuser = true;
        // Use super_admin role if exists
        const superAdminRole = roles.find((r) => r.user_level === "super_admin");
        if (superAdminRole) {
          createData.role_id = superAdminRole.id;
        } else {
          createData.role_id = form.role_id;
        }
      } else {
        createData.is_superuser = false;
        createData.role_id = form.role_id;
      }

      await userApi.createUser({
        userCreate: createData,
      });
      notify("User created successfully", { type: "success" });
      onSuccess();
      onClose();
    } catch (error: any) {
      notify(error?.response?.data?.detail || "Error creating user", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <PersonAddIcon />
        Create New User
        <IconButton
          onClick={onClose}
          sx={{ ml: "auto" }}
          disabled={saving}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Add a new user to the system and assign their role and permissions.
        </Typography>

        <TextField
          margin="normal"
          label="Full Name"
          name="full_name"
          placeholder="e.g., John Doe"
          fullWidth
          value={form.full_name}
          onChange={handleChange}
          autoFocus
        />

        <TextField
          margin="normal"
          label="Email Address"
          name="email"
          type="email"
          placeholder="e.g., john.doe@example.com"
          fullWidth
          required
          value={form.email}
          onChange={handleChange}
          error={!!emailError}
          helperText={emailError}
        />

        <TextField
          margin="normal"
          label="Password"
          name="password"
          type="password"
          placeholder="Enter password"
          fullWidth
          required
          value={form.password}
          onChange={handleChange}
        />

        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            User Level
          </Typography>
          <RadioGroup value={userLevel} onChange={handleUserLevelChange}>
            {currentUserIsSuperAdmin && (
              <FormControlLabel
                value="super_admin"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      Org Admin{" "}
                      <Chip label="Highest Access" size="small" color="error" />
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Full system access including organization and users management
                    </Typography>
                  </Box>
                }
              />
            )}
            <FormControlLabel
              value="user"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    User{" "}
                    <Chip label="Standard Access" size="small" color="default" />
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Regular user with limited permissions
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Box>

        {currentUserIsSuperAdmin && (
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Organization</InputLabel>
            <Select
              name="organization_id"
              value={form.organization_id}
              onChange={handleChange as any}
              label="Organization"
            >
              <MenuItem value="">
                <em>Select organization</em>
              </MenuItem>
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Hide role dropdown for super_admin - auto assigned */}
        {userLevel !== "super_admin" && (
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Role</InputLabel>
            <Select
              name="role_id"
              value={form.role_id}
              onChange={handleChange as any}
              label="Role"
              disabled={!form.organization_id}
            >
              <MenuItem value="">
                <em>Select role</em>
              </MenuItem>
              {getFilteredRoles().map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {userLevel === "super_admin" && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Org Admin role will be automatically assigned with full system access.
          </Alert>
        )}

        {userLevel === "user" && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Users have restricted permissions and cannot create other users.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          variant="contained"
          color="primary"
        >
          Create User
        </Button>
      </DialogActions>
    </Dialog>
  );
};